import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    MagnifyingGlass, Sparkle, Plus, CheckSquare, Square, Tag, DownloadSimple, X,
    CaretLeft, CaretRight, Stack, ListBullets, Rows, Archive, MapPin, CloudArrowDown, Lightning,
} from "@phosphor-icons/react";

const PAGE_SIZE = 50;
const BATCH_SIZE = 500;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function LeadFinder() {
    const [catalog, setCatalog] = useState({ keywords: [], service_tags: [], states: [], sources: [] });
    const [source, setSource] = useState("mock");
    const [enrichEmails, setEnrichEmails] = useState(false);
    const [keywords, setKeywords] = useState(["land surveyor", "CAD drafting services"]);
    const [customKw, setCustomKw] = useState("");
    const [selectedStates, setSelectedStates] = useState(["Florida", "Texas", "Illinois", "Georgia", "Maryland", "Ohio"]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [results, setResults] = useState([]);
    const [byState, setByState] = useState({});
    const [stateFilter, setStateFilter] = useState("");
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [importing, setImporting] = useState(false);
    const [currentBatch, setCurrentBatch] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [viewMode, setViewMode] = useState("grouped"); // "flat" | "grouped"
    const [collapsedStates, setCollapsedStates] = useState(new Set());
    const [exportingZip, setExportingZip] = useState(false);
    const [exportAllOpen, setExportAllOpen] = useState(false);
    const [exportAllLoading, setExportAllLoading] = useState(false);
    const [exportAllPerState, setExportAllPerState] = useState(10000);
    const [exportAllBatchSize, setExportAllBatchSize] = useState(10000);
    const [searchWarning, setSearchWarning] = useState("");

    useEffect(() => {
        document.title = "Surveys2CAD | Lead Finder";
        api.get("/admin/lead-finder/keywords").then((r) => setCatalog(r.data)).catch(() => {});
        // Mount-only fetch. `api` is a module import, `setCatalog` is a stable
        // React setter — intentionally omitted from deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggle = (arr, setArr, v) => {
        setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
    };

    const addCustomKeyword = () => {
        const v = customKw.trim();
        if (v && !keywords.includes(v)) setKeywords([...keywords, v]);
        setCustomKw("");
    };

    const fetchBatch = async (batchNum) => {
        const isReal = source !== "mock";
        const { data } = await api.post("/admin/lead-finder/search", {
            keywords, states: selectedStates, service_tags: selectedServices,
            per_state_limit: BATCH_SIZE,
            batch: batchNum,
            limit: 50000,
            source,
            enrich: isReal && enrichEmails,
        }, { timeout: isReal && enrichEmails ? 180000 : 90000 });
        return data;
    };

    const search = async () => {
        if (!keywords.length && !selectedStates.length && !selectedServices.length) {
            toast.error("Pick at least one keyword, state, or service");
            return;
        }
        setLoading(true);
        setSearchWarning("");
        try {
            const data = await fetchBatch(1);
            setResults(data.results);
            setByState(data.by_state || {});
            setCurrentBatch(1);
            setHasMore(!!data.has_more);
            setSelected(new Set());
            setStateFilter("");
            setPage(1);
            if (data.warning) setSearchWarning(data.warning);
            const isReal = source !== "mock";
            const srcLabel = isReal ? (source === "all" ? "Yellow Pages + BBB" : source) : "mock";
            if (data.results.length > 0) {
                toast.success(`${isReal ? "Real" : "Batch 1"} · ${data.results.length} leads from ${srcLabel} across ${Object.keys(data.by_state || {}).length} states`);
            } else if (data.warning) {
                toast.warning(data.warning);
            } else {
                toast.info("No results for this query");
            }
        } catch {
            toast.error("Search failed");
        } finally { setLoading(false); }
    };

    const loadNextBatch = async () => {
        if (loadingMore || !hasMore) return;
        const nextBatch = currentBatch + 1;
        setLoadingMore(true);
        try {
            const data = await fetchBatch(nextBatch);
            if (!data.results?.length) {
                setHasMore(false);
                toast.info("No more leads available for this query.");
                return;
            }
            // Merge with existing — dedupe by id (IDs are deterministic per batch/variant)
            const existingIds = new Set(results.map((r) => r.id));
            const fresh = data.results.filter((r) => !existingIds.has(r.id));
            const combined = [...results, ...fresh];
            // Recompute per-state breakdown
            const nextByState = {};
            combined.forEach((r) => { nextByState[r.state] = (nextByState[r.state] || 0) + 1; });
            setResults(combined);
            setByState(nextByState);
            setCurrentBatch(nextBatch);
            setHasMore(!!data.has_more);
            toast.success(`Batch ${nextBatch} added · +${fresh.length} leads (${combined.length} total)`);
        } catch {
            toast.error("Failed to load next batch");
        } finally { setLoadingMore(false); }
    };

    // Client-side filter + pagination
    const filteredResults = useMemo(
        () => (stateFilter ? results.filter((r) => r.state === stateFilter) : results),
        [results, stateFilter],
    );
    const totalPages = Math.max(1, Math.ceil(filteredResults.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageStart = (currentPage - 1) * PAGE_SIZE;
    const pageResults = filteredResults.slice(pageStart, pageStart + PAGE_SIZE);

    const toggleSelect = (id) => {
        const s = new Set(selected);
        if (s.has(id)) s.delete(id); else s.add(id);
        setSelected(s);
    };
    const selectAllOnPage = () => {
        const s = new Set(selected);
        pageResults.forEach((r) => s.add(r.id));
        setSelected(s);
    };
    const selectAllFiltered = () => setSelected(new Set(filteredResults.map((r) => r.id)));
    const clearAll = () => setSelected(new Set());

    const importSelected = async () => {
        if (!selected.size) return toast.error("Select leads first");
        setImporting(true);
        try {
            const { data } = await api.post("/admin/lead-finder/import", {
                lead_ids: Array.from(selected),
                all_leads: results,
                tags: ["lead-finder"],
            });
            toast.success(`${data.inserted} added to Contacts · ${data.skipped} skipped`);
            setSelected(new Set());
        } catch {
            toast.error("Import failed");
        } finally { setImporting(false); }
    };

    const exportCsv = () => {
        if (!results.length) return toast.error("Run a search first");
        const rows = [["name", "email", "phone", "company", "role", "state", "county", "keyword", "services", "confidence"]];
        filteredResults.forEach((r) => rows.push([
            r.name, r.email, r.phone, r.company, r.role, r.state, r.county || "",
            r.source_keyword, (r.matched_services || []).join(";"), r.confidence,
        ]));
        const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "lead-finder-results.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportByStateZip = async () => {
        if (!results.length) return toast.error("Run a search first");
        setExportingZip(true);
        try {
            const response = await api.post(
                "/admin/lead-finder/export-by-state",
                { leads: filteredResults },
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(response.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `lead-finder-by-state-${new Date().toISOString().slice(0, 10)}.zip`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`ZIP with ${Object.keys(byState).length} state files downloaded`);
        } catch {
            toast.error("Export failed");
        } finally { setExportingZip(false); }
    };

    const exportAllLeadsZip = async () => {
        if (!selectedStates.length) {
            toast.error("Pick at least one state (left sidebar) or leave empty to use all 51");
            return;
        }
        const est = exportAllPerState * (selectedStates.length || 51);
        if (est > 200000 && !window.confirm(
            `This will generate ~${est.toLocaleString()} leads and may take 10+ seconds. Continue?`
        )) return;
        setExportAllLoading(true);
        try {
            const response = await api.post(
                "/admin/lead-finder/export-all",
                {
                    keywords,
                    states: selectedStates,
                    service_tags: selectedServices,
                    leads_per_state: exportAllPerState,
                    batch_size: exportAllBatchSize,
                },
                { responseType: "blob", timeout: 120000 },
            );
            const totalLeads = response.headers?.["x-total-leads"];
            const totalBatches = response.headers?.["x-total-batches"];
            const url = URL.createObjectURL(response.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `surveys2cad-all-leads-${new Date().toISOString().slice(0, 10)}.zip`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`Exported ${totalLeads || "all"} leads in ${totalBatches || "?"} batches · sorted state → county → name`);
            setExportAllOpen(false);
        } catch (err) {
            toast.error(err?.response?.status === 400 ? "No leads generated" : "Export failed");
        } finally { setExportAllLoading(false); }
    };

    // Group pageResults by state for the grouped view.
    const groupedPageResults = useMemo(() => {
        const out = {};
        pageResults.forEach((r) => {
            const k = r.state || "Unknown";
            (out[k] = out[k] || []).push(r);
        });
        return out;
    }, [pageResults]);

    const toggleStateCollapse = (st) => {
        const next = new Set(collapsedStates);
        if (next.has(st)) next.delete(st); else next.add(st);
        setCollapsedStates(next);
    };

    const renderLeadCard = (r) => {
        const isSel = selected.has(r.id);
        return (
            <div
                key={r.id}
                className={`tech-card p-4 cursor-pointer transition-all ${isSel ? "!border-cyan-500/60 bg-cyan-500/5" : ""}`}
                onClick={() => toggleSelect(r.id)}
                data-testid={`lf-lead-${r.id}`}
            >
                <div className="flex items-start gap-3">
                    <button className="pt-0.5 flex-shrink-0" aria-label="Select">
                        {isSel ? <CheckSquare size={20} weight="fill" className="text-cyan-400" />
                               : <Square size={20} className="text-slate-500" />}
                    </button>
                    <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="md:col-span-2">
                            <div className="font-heading font-semibold text-slate-50 text-sm flex items-center gap-2 flex-wrap">
                                {r.name}
                                {r.source && r.source !== "mock" && (
                                    <span
                                        className={`px-1.5 py-0.5 rounded text-[9px] font-mono-alt uppercase tracking-wider ${
                                            r.source === "yellowpages"
                                                ? "bg-yellow-500/15 text-yellow-300 border border-yellow-500/30"
                                                : r.source === "bbb"
                                                    ? "bg-blue-500/15 text-blue-300 border border-blue-500/30"
                                                    : "bg-purple-500/15 text-purple-300 border border-purple-500/30"
                                        }`}
                                        title={`Real lead from ${r.source}`}
                                        data-testid={`lf-source-badge-${r.source}`}
                                    >
                                        {r.source === "yellowpages" ? "YP" : r.source === "bbb" ? "BBB" : r.source}
                                    </span>
                                )}
                                {r.rating && (
                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-mono-alt bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" title={r.rating}>
                                        {r.rating.replace("BBB Rating: ", "★ ")}
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-slate-400">{r.role} · <span className="text-cyan-300">{r.company}</span></div>
                            <div className="text-[10px] text-slate-500 font-mono-alt mt-1 flex items-center gap-2 flex-wrap">
                                <span>{r.state} · {r.state_abbr}</span>
                                {r.county && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-300 border border-orange-500/30">
                                        <MapPin size={10} weight="fill" /> {r.county}
                                    </span>
                                )}
                                {r.website && (
                                    <a
                                        href={r.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="underline decoration-dotted text-cyan-400 hover:text-cyan-200"
                                    >
                                        website ↗
                                    </a>
                                )}
                            </div>
                        </div>
                        <div>
                            <div className="text-xs text-slate-300 font-mono-alt break-all">{r.email}</div>
                            <div className="text-xs text-slate-400 font-mono-alt mt-0.5">{r.phone}</div>
                        </div>
                        <div className="text-right">
                            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px] font-mono-alt">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {r.confidence}% match
                            </div>
                            <div className="text-[10px] text-slate-500 mt-1 font-mono-alt">via "{r.source_keyword}"</div>
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-3 ml-8">
                    {(r.matched_services || []).slice(0, 5).map((s) => (
                        <span key={s} className="px-2 py-0.5 rounded-full text-[10px] font-mono-alt bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">
                            {s}
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="admin-lead-finder">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="overline mb-2 flex items-center gap-2">
                        <Sparkle size={14} className="text-orange-400" weight="fill" /> New · Prospecting
                    </div>
                    <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-50 tracking-tight">Lead Finder</h1>
                    <p className="text-slate-400 mt-2 text-sm">
                        Discover land-surveying & CAD-drafting prospects by keyword, state, and service. Add matches directly to your contacts.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Search builder */}
                <div className="lg:col-span-1 tech-card p-5 h-fit sticky top-6">
                    <div className="overline mb-2">Source</div>
                    <select
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-cyan-500 mb-1"
                        data-testid="lf-source-select"
                    >
                        {(catalog.sources || []).length === 0 && (
                            <option value="mock">Mock · Fast</option>
                        )}
                        {(catalog.sources || []).map((s) => (
                            <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                    </select>
                    <p className="text-[10px] text-slate-500 mb-4 leading-relaxed">
                        {(catalog.sources || []).find((s) => s.id === source)?.note
                            || "Choose a lead source."}
                        {source !== "mock" && (
                            <span className="block mt-1 text-orange-300">Real scraping runs live — expect a single batch and slower response.</span>
                        )}
                    </p>

                    {source !== "mock" && (
                        <label className="flex items-start gap-2 mb-4 p-2 bg-purple-500/5 border border-purple-500/30 rounded-lg cursor-pointer">
                            <input
                                type="checkbox"
                                checked={enrichEmails}
                                onChange={(e) => setEnrichEmails(e.target.checked)}
                                className="mt-0.5 accent-purple-500"
                                data-testid="lf-enrich-toggle"
                            />
                            <span className="text-[11px] leading-relaxed text-slate-300">
                                <span className="text-purple-200 font-mono-alt uppercase tracking-wider text-[10px]">Enrich emails</span><br />
                                Visit each lead's website / profile and extract <span className="text-cyan-300">mailto:</span> contact emails. Adds 30-90s but doubles email coverage on BBB and listings without a public email.
                            </span>
                        </label>
                    )}

                    <div className="overline mb-3">Keywords</div>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                        {keywords.map((k) => (
                            <span key={k} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-200 text-[11px] font-mono-alt">
                                {k}
                                <button onClick={() => setKeywords(keywords.filter((x) => x !== k))} className="hover:text-red-300">
                                    <X size={10} />
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2 mb-3">
                        <input
                            type="text"
                            value={customKw}
                            onChange={(e) => setCustomKw(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addCustomKeyword()}
                            placeholder="Add custom keyword…"
                            className="flex-1 bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-cyan-500"
                            data-testid="lf-custom-keyword"
                        />
                        <button onClick={addCustomKeyword} className="btn-ghost !py-2 text-xs"><Plus size={14} /></button>
                    </div>
                    <div className="max-h-32 overflow-y-auto border border-slate-800 rounded-lg p-2 mb-4">
                        <div className="flex flex-wrap gap-1">
                            {catalog.keywords.slice(0, 40).map((k) => (
                                <button
                                    key={k}
                                    onClick={() => !keywords.includes(k) && setKeywords([...keywords, k])}
                                    disabled={keywords.includes(k)}
                                    className="px-2 py-0.5 rounded text-[10px] font-mono-alt bg-slate-800 text-slate-400 hover:bg-cyan-500/20 hover:text-cyan-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    + {k}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="overline mb-2">Services</div>
                    <div className="flex flex-wrap gap-1 mb-4 max-h-28 overflow-y-auto">
                        {catalog.service_tags.map((s) => (
                            <button
                                key={s}
                                onClick={() => toggle(selectedServices, setSelectedServices, s)}
                                className={`px-2 py-0.5 rounded text-[10px] font-mono-alt border ${
                                    selectedServices.includes(s)
                                        ? "bg-orange-500/20 border-orange-400/50 text-orange-200"
                                        : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200"
                                }`}
                                data-testid={`lf-service-${s}`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>

                    <div className="overline mb-2">States / Provinces</div>
                    <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto mb-4">
                        {catalog.states.map((st) => (
                            <label key={st} className="flex items-center gap-1.5 cursor-pointer text-[11px] text-slate-300 hover:text-cyan-300">
                                <input
                                    type="checkbox"
                                    checked={selectedStates.includes(st)}
                                    onChange={() => toggle(selectedStates, setSelectedStates, st)}
                                    className="accent-cyan-500 w-3 h-3"
                                />
                                {st}
                            </label>
                        ))}
                    </div>

                    <div className="bg-slate-950/60 border border-slate-800 rounded-lg p-3 mb-4 text-[11px] text-slate-400 leading-relaxed">
                        {source === "mock" ? (
                            <>
                                <span className="text-cyan-300 font-mono-alt">{BATCH_SIZE} leads/state per batch</span> — click <span className="text-orange-300 font-mono-alt">"Load next batch"</span> below the results to fetch more until all contacts are surfaced.
                            </>
                        ) : (
                            <>
                                <span className="text-cyan-300 font-mono-alt">Real scrape</span> · fetches <span className="text-orange-300 font-mono-alt">up to ~30/state (YP) + ~15/state (BBB)</span> per search. Single batch — re-run with different keywords for more coverage.
                            </>
                        )}
                    </div>

                    <button
                        onClick={search}
                        disabled={loading}
                        className="btn-primary w-full justify-center"
                        data-testid="lf-search-btn"
                    >
                        <MagnifyingGlass size={16} weight="bold" /> {loading ? "Searching…" : "Find Leads"}
                    </button>

                    <div className="mt-4 pt-4 border-t border-dashed border-slate-700">
                        <button
                            onClick={() => setExportAllOpen((v) => !v)}
                            className="w-full flex items-center justify-between gap-2 text-xs text-orange-300 hover:text-orange-200 font-mono-alt uppercase tracking-wider py-1"
                            data-testid="lf-export-all-toggle"
                        >
                            <span className="flex items-center gap-2">
                                <Lightning size={14} weight="fill" />
                                Export ALL leads (one click)
                            </span>
                            <span className="text-[10px]">{exportAllOpen ? "▾" : "▸"}</span>
                        </button>
                        {exportAllOpen && (
                            <div className="mt-3 space-y-3 bg-slate-950/70 border border-slate-800 rounded-lg p-3" data-testid="lf-export-all-panel">
                                <p className="text-[11px] text-slate-400 leading-relaxed">
                                    Generate <span className="text-cyan-300 font-mono-alt">every</span> lead for your selected states
                                    ({selectedStates.length || "all 51"}) and download as a ZIP with CSV batches sorted <span className="text-cyan-300 font-mono-alt">state → county → name</span>.
                                </p>
                                <div>
                                    <label className="overline block mb-1">Leads per state</label>
                                    <input
                                        type="number" min={100} max={50000} step={500}
                                        value={exportAllPerState}
                                        onChange={(e) => setExportAllPerState(Math.min(50000, Math.max(100, Number(e.target.value) || 10000)))}
                                        className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-100 font-mono-alt focus:outline-none focus:border-cyan-500"
                                        data-testid="lf-export-all-per-state"
                                    />
                                </div>
                                <div>
                                    <label className="overline block mb-1">Batch size (rows/CSV)</label>
                                    <select
                                        value={exportAllBatchSize}
                                        onChange={(e) => setExportAllBatchSize(Number(e.target.value))}
                                        className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-100 font-mono-alt focus:outline-none focus:border-cyan-500"
                                        data-testid="lf-export-all-batch-size"
                                    >
                                        <option value={1000}>1,000 rows / CSV</option>
                                        <option value={5000}>5,000 rows / CSV</option>
                                        <option value={10000}>10,000 rows / CSV</option>
                                        <option value={25000}>25,000 rows / CSV</option>
                                        <option value={50000}>50,000 rows / CSV</option>
                                    </select>
                                </div>
                                <div className="text-[10px] text-slate-500 font-mono-alt bg-slate-900/70 rounded px-2 py-1.5 leading-relaxed">
                                    Estimate: <span className="text-orange-300">
                                        ~{((exportAllPerState * (selectedStates.length || 51))).toLocaleString()}
                                    </span> leads ·{" "}
                                    <span className="text-cyan-300">
                                        {Math.ceil(exportAllPerState * (selectedStates.length || 51) / exportAllBatchSize)}
                                    </span> batch CSV{Math.ceil(exportAllPerState * (selectedStates.length || 51) / exportAllBatchSize) === 1 ? "" : "s"}
                                </div>
                                <button
                                    onClick={exportAllLeadsZip}
                                    disabled={exportAllLoading}
                                    className="w-full btn-primary !py-2 justify-center text-xs disabled:opacity-50"
                                    data-testid="lf-export-all-submit"
                                >
                                    <CloudArrowDown size={14} weight="bold" />
                                    {exportAllLoading ? "Generating ZIP…" : "Download All (ZIP)"}
                                </button>
                                <p className="text-[10px] text-slate-600 leading-relaxed">
                                    ZIP contains: <span className="text-slate-400">_summary.csv</span>,
                                    <span className="text-slate-400"> by-state/*.csv</span>, and
                                    <span className="text-slate-400"> batches/all-leads-batch-NN.csv</span>.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Results */}
                <div className="lg:col-span-2">
                    {searchWarning && (
                        <div className="mb-4 px-3 py-2 rounded-lg bg-orange-500/10 border border-orange-500/30 text-xs text-orange-200 font-mono-alt" data-testid="lf-search-warning">
                            ⚠ {searchWarning}
                        </div>
                    )}
                    {results.length > 0 && (
                        <>
                            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                                <div className="text-sm text-slate-400">
                                    <span className="text-cyan-300 font-heading font-bold">{filteredResults.length}</span>
                                    {stateFilter && <span> in {stateFilter}</span>}
                                    {!stateFilter && results.length !== filteredResults.length && <span> of {results.length}</span>}
                                    {" "}leads ·
                                    <span className="text-orange-300 font-heading font-bold"> {selected.size}</span> selected
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <div className="inline-flex rounded-lg border border-slate-800 overflow-hidden mr-1" data-testid="lf-view-toggle">
                                        <button
                                            onClick={() => setViewMode("grouped")}
                                            className={`px-2 py-1.5 text-xs flex items-center gap-1 ${viewMode === "grouped" ? "bg-cyan-500/20 text-cyan-100" : "text-slate-400 hover:text-slate-200"}`}
                                            data-testid="lf-view-grouped"
                                            title="Group by state"
                                        >
                                            <Rows size={13} weight="bold" /> Grouped
                                        </button>
                                        <button
                                            onClick={() => setViewMode("flat")}
                                            className={`px-2 py-1.5 text-xs flex items-center gap-1 ${viewMode === "flat" ? "bg-cyan-500/20 text-cyan-100" : "text-slate-400 hover:text-slate-200"}`}
                                            data-testid="lf-view-flat"
                                            title="Flat list"
                                        >
                                            <ListBullets size={13} weight="bold" /> Flat
                                        </button>
                                    </div>
                                    <button onClick={selectAllOnPage} className="btn-ghost !py-1.5 text-xs" data-testid="lf-select-page">Select page</button>
                                    <button onClick={selectAllFiltered} className="btn-ghost !py-1.5 text-xs" data-testid="lf-select-all">Select all ({filteredResults.length})</button>
                                    <button onClick={clearAll} className="btn-ghost !py-1.5 text-xs">Clear</button>
                                    <button onClick={exportCsv} className="btn-ghost !py-1.5 text-xs" data-testid="lf-export-csv"><DownloadSimple size={14} /> CSV</button>
                                    <button
                                        onClick={exportByStateZip}
                                        disabled={exportingZip}
                                        className="btn-ghost !py-1.5 text-xs disabled:opacity-50"
                                        data-testid="lf-export-by-state-zip"
                                        title="Download ZIP with one CSV per state (includes counties)"
                                    >
                                        <Archive size={14} /> {exportingZip ? "Zipping…" : "By State (ZIP)"}
                                    </button>
                                    <button
                                        onClick={importSelected}
                                        disabled={!selected.size || importing}
                                        className="btn-primary !py-1.5 text-xs"
                                        data-testid="lf-import-btn"
                                    >
                                        <Tag size={14} weight="bold" /> {importing ? "Adding…" : `Add ${selected.size || ""} to Contacts`}
                                    </button>
                                </div>
                            </div>

                            {/* Per-state breakdown chips */}
                            {Object.keys(byState).length > 1 && (
                                <div className="flex flex-wrap gap-1.5 mb-4" data-testid="lf-state-breakdown">
                                    <button
                                        onClick={() => { setStateFilter(""); setPage(1); }}
                                        className={`px-2.5 py-1 rounded-full text-[11px] font-mono-alt border transition-colors ${
                                            stateFilter === ""
                                                ? "bg-cyan-500/20 border-cyan-400/60 text-cyan-100"
                                                : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200"
                                        }`}
                                    >
                                        All · {results.length}
                                    </button>
                                    {Object.entries(byState)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([st, n]) => (
                                            <button
                                                key={st}
                                                onClick={() => { setStateFilter(st === stateFilter ? "" : st); setPage(1); }}
                                                className={`px-2.5 py-1 rounded-full text-[11px] font-mono-alt border transition-colors ${
                                                    stateFilter === st
                                                        ? "bg-orange-500/20 border-orange-400/60 text-orange-100"
                                                        : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200"
                                                }`}
                                                data-testid={`lf-state-chip-${st}`}
                                            >
                                                {st} · {n}
                                            </button>
                                        ))}
                                </div>
                            )}
                        </>
                    )}

                    {results.length === 0 ? (
                        <div className="tech-card p-10 text-center">
                            <MagnifyingGlass size={40} weight="duotone" className="text-cyan-400 mx-auto mb-4" />
                            <h3 className="font-heading text-xl font-semibold text-slate-50 mb-2">Start prospecting</h3>
                            <p className="text-slate-400 text-sm max-w-md mx-auto">
                                Pick keywords (e.g. <span className="font-mono-alt text-cyan-300">boundary survey</span>,
                                <span className="font-mono-alt text-cyan-300"> ALTA/NSPS</span>), target states, and service types.
                                We'll surface land-surveying firms and CAD-drafting prospects matching your criteria.
                            </p>
                        </div>
                    ) : (
                        <>
                            {viewMode === "flat" ? (
                                <div className="grid grid-cols-1 gap-3" data-testid="lf-results-flat">
                                    {pageResults.map((r) => renderLeadCard(r))}
                                </div>
                            ) : (
                                <div className="space-y-5" data-testid="lf-results-grouped">
                                    {Object.entries(groupedPageResults)
                                        .sort((a, b) => a[0].localeCompare(b[0]))
                                        .map(([st, leads]) => {
                                            const isCollapsed = collapsedStates.has(st);
                                            const totalInState = byState[st] || leads.length;
                                            return (
                                                <div key={st} className="tech-card !p-0 overflow-hidden" data-testid={`lf-state-group-${st}`}>
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleStateCollapse(st)}
                                                        className="w-full flex items-center justify-between px-4 py-3 bg-slate-900/80 border-b border-slate-800 hover:bg-slate-900 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <MapPin size={18} weight="fill" className="text-orange-400" />
                                                            <div className="font-heading font-semibold text-slate-50 text-sm">{st}</div>
                                                            <div className="text-[11px] text-slate-400 font-mono-alt">
                                                                {leads.length} on page · {totalInState} total
                                                            </div>
                                                        </div>
                                                        <div className="text-[10px] text-slate-500 font-mono-alt">
                                                            {isCollapsed ? "▸ Expand" : "▾ Collapse"}
                                                        </div>
                                                    </button>
                                                    {!isCollapsed && (
                                                        <div className="p-3 space-y-2 bg-slate-950/40">
                                                            {leads.map((r) => renderLeadCard(r))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </div>
                            )}

                            {totalPages > 1 && (
                                <div className="mt-6 flex items-center justify-between flex-wrap gap-3" data-testid="lf-pagination">
                                    <div className="text-xs text-slate-400 font-mono-alt">
                                        Showing <span className="text-cyan-300">{pageStart + 1}</span>–
                                        <span className="text-cyan-300">{Math.min(pageStart + PAGE_SIZE, filteredResults.length)}</span>
                                        {" "}of <span className="text-cyan-300">{filteredResults.length}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="btn-ghost !py-1.5 !px-2 text-xs disabled:opacity-40"
                                            data-testid="lf-prev-page"
                                        >
                                            <CaretLeft size={14} weight="bold" />
                                        </button>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                                            .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                                            .reduce((acc, p, i, arr) => {
                                                if (i > 0 && p - arr[i - 1] > 1) acc.push("…");
                                                acc.push(p);
                                                return acc;
                                            }, [])
                                            .map((p, i) => (
                                                p === "…" ? (
                                                    <span key={`gap-${i}`} className="px-2 text-slate-600 text-xs">…</span>
                                                ) : (
                                                    <button
                                                        key={p}
                                                        onClick={() => setPage(p)}
                                                        className={`min-w-[32px] px-2 py-1.5 rounded text-xs font-mono-alt border ${
                                                            p === currentPage
                                                                ? "bg-cyan-500/20 border-cyan-400/60 text-cyan-100"
                                                                : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-100"
                                                        }`}
                                                        data-testid={`lf-page-${p}`}
                                                    >
                                                        {p}
                                                    </button>
                                                )
                                            ))}
                                        <button
                                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            className="btn-ghost !py-1.5 !px-2 text-xs disabled:opacity-40"
                                            data-testid="lf-next-page"
                                        >
                                            <CaretRight size={14} weight="bold" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="mt-6 flex flex-col items-center gap-2" data-testid="lf-load-more-section">
                                {hasMore ? (
                                    <button
                                        onClick={loadNextBatch}
                                        disabled={loadingMore}
                                        className="btn-primary !py-3 !px-6 text-sm justify-center"
                                        data-testid="lf-load-next-batch"
                                    >
                                        <Stack size={16} weight="bold" />
                                        {loadingMore
                                            ? `Loading batch ${currentBatch + 1}…`
                                            : `Load next batch (batch ${currentBatch + 1} · +${BATCH_SIZE}/state)`}
                                    </button>
                                ) : (
                                    currentBatch > 0 && (
                                        <div className="text-xs text-slate-500 font-mono-alt">
                                            All available leads loaded · {results.length} total across {currentBatch} batch{currentBatch > 1 ? "es" : ""}
                                        </div>
                                    )
                                )}
                                {currentBatch > 0 && (
                                    <div className="text-[11px] text-slate-600 font-mono-alt">
                                        Current: batch {currentBatch} · {BATCH_SIZE} leads/state/batch · {results.length} lead{results.length === 1 ? "" : "s"} accumulated
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
